import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import Button from "../components/Inputs/Button";
import Select from "../components/Inputs/Select";
import Input from "../components/Inputs/Input";
import Layout from "src/components/Layout";
import {
  getAllTasks,
  createTask,
  updateTask,
  deleteTask,
  getClients,
} from "src/services/client";
import { Client } from "src/types";
import { Link } from "react-router-dom";

interface Task {
  id: string;
  description: string;
  assignee?: string;
  dueDate?: string;
  completed: boolean;
  createdAt: string;
  updatedAt: string;
  clientId: number;
  client: {
    id: number;
    title: string;
  };
}

type SortField =
  | "description"
  | "assigneeType"
  | "dueDate"
  | "createdAt"
  | "clientName";
type SortDirection = "asc" | "desc";

const AllTasks = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [clients, setClients] = useState<Client[]>([]);

  useEffect(() => {
    // Load tasks and clients
    Promise.all([getAllTasks(), getClients()])
      .then(([tasksData, clientsData]) => {
        setTasks(tasksData || []);
        setClients(clientsData || []);
      })
      .catch((error) => {
        console.error("Error loading data:", error);
      });
  }, []);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>("dueDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [filterAssigneeType, setFilterAssigneeType] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterClient, setFilterClient] = useState<string>("all");

  const SortIcon = ({ name, item: key }: { name: string; item: string }) => {
    const handleClick = () => {
      if (sortField === key) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else {
        setSortField(key as SortField);
        setSortDirection("asc");
      }
    };

    return (
      <div
        className="w-full cursor-pointer flex items-center justify-center gap-3 py-3"
        onClick={handleClick}
      >
        {name}
        <div className="inline-block">
          <div className="flex flex-col">
            <ChevronUpIcon
              className={
                sortField == key && sortDirection == "asc"
                  ? "text-black mb-[-3px] w-4"
                  : "text-gray-400 mb-[-3px] w-4"
              }
            />
            <ChevronDownIcon
              className={
                sortField == key && sortDirection == "desc"
                  ? "text-black w-4"
                  : "text-gray-400 w-4"
              }
            />
          </div>
        </div>
      </div>
    );
  };

  const [newTask, setNewTask] = useState({
    description: "",
    assignee: "advisor" as const,
    dueDate: "",
    clientId: 0,
  });

  const [editTask, setEditTask] = useState({
    description: "",
    assignee: "advisor" as const,
    dueDate: "",
    clientId: 0,
  });

  const addTask = async () => {
    if (!newTask.description.trim() || !newTask.clientId) return;

    const selectedClient = clients.find((c) => c.id === newTask.clientId);
    if (!selectedClient) return;

    // Create optimistic task for instant UI update
    const optimisticTask: Task = {
      id: `temp-${Date.now()}`,
      description: newTask.description,
      assignee: newTask.assignee,
      dueDate: newTask.dueDate || undefined,
      completed: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      clientId: newTask.clientId,
      client: {
        id: selectedClient.id,
        title: selectedClient.title,
      },
    };

    // Update UI instantly
    setTasks([...tasks, optimisticTask]);
    setNewTask({
      description: "",
      assignee: "advisor",
      dueDate: "",
      clientId: 0,
    });
    setShowAddForm(false);

    // Make API call in background
    try {
      const response = await createTask({
        description: newTask.description,
        assignee: newTask.assignee,
        dueDate: newTask.dueDate || undefined,
        clientId: newTask.clientId,
      });

      if (response.ok) {
        const createdTask = await response.json();
        // Replace optimistic task with real task
        setTasks((prevTasks) =>
          prevTasks.map((task) =>
            task.id === optimisticTask.id ? createdTask : task,
          ),
        );
      } else {
        // Remove optimistic task on error
        setTasks((prevTasks) =>
          prevTasks.filter((task) => task.id !== optimisticTask.id),
        );
        console.error("Failed to create task");
      }
    } catch (error) {
      // Remove optimistic task on error
      setTasks((prevTasks) =>
        prevTasks.filter((task) => task.id !== optimisticTask.id),
      );
      console.error("Error creating task:", error);
    }
  };

  const handleDeleteTask = async (id: string) => {
    // Update UI instantly
    const taskToDelete = tasks.find((task) => task.id === id);
    setTasks(tasks.filter((task) => task.id !== id));

    // Make API call in background
    try {
      const response = await deleteTask(id);
      if (!response.ok) {
        // Restore task on error
        if (taskToDelete) {
          setTasks((prevTasks) => [...prevTasks, taskToDelete]);
        }
        console.error("Failed to delete task");
      }
    } catch (error) {
      // Restore task on error
      if (taskToDelete) {
        setTasks((prevTasks) => [...prevTasks, taskToDelete]);
      }
      console.error("Error deleting task:", error);
    }
  };

  const toggleComplete = async (id: string) => {
    const task = tasks.find((t) => t.id === id);
    if (!task) return;

    const newCompleted = !task.completed;

    // Update UI instantly
    setTasks(
      tasks.map((task) =>
        task.id === id ? { ...task, completed: newCompleted } : task,
      ),
    );

    // Make API call in background
    try {
      const response = await updateTask(id, { completed: newCompleted });
      if (!response.ok) {
        // Revert on error
        setTasks(
          tasks.map((task) =>
            task.id === id ? { ...task, completed: !newCompleted } : task,
          ),
        );
        console.error("Failed to update task");
      }
    } catch (error) {
      // Revert on error
      setTasks(
        tasks.map((task) =>
          task.id === id ? { ...task, completed: !newCompleted } : task,
        ),
      );
      console.error("Error updating task:", error);
    }
  };

  const startEditing = (task: Task) => {
    setEditingTaskId(task.id);
    setEditTask({
      description: task.description,
      assignee: task.assignee || ("advisor" as any),
      dueDate: task.dueDate || "",
      clientId: task.clientId,
    });
  };

  const saveEdit = async () => {
    if (!editTask.description.trim() || !editTask.clientId || !editingTaskId)
      return;

    const selectedClient = clients.find((c) => c.id === editTask.clientId);
    if (!selectedClient) return;

    const originalTask = tasks.find((t) => t.id === editingTaskId);

    // Update UI instantly
    setTasks(
      tasks.map((task) =>
        task.id === editingTaskId
          ? {
              ...task,
              description: editTask.description,
              assignee: editTask.assignee,
              dueDate: editTask.dueDate || undefined,
              clientId: editTask.clientId,
              client: {
                id: selectedClient.id,
                title: selectedClient.title,
              },
            }
          : task,
      ),
    );

    const editingId = editingTaskId;
    setEditingTaskId(null);
    setEditTask({
      description: "",
      assignee: "advisor",
      dueDate: "",
      clientId: 0,
    });

    // Make API call in background
    try {
      const response = await updateTask(editingId, {
        description: editTask.description,
        assignee: editTask.assignee,
        dueDate: editTask.dueDate || undefined,
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTasks((prevTasks) =>
          prevTasks.map((task) => (task.id === editingId ? updatedTask : task)),
        );
      } else {
        // Revert on error
        if (originalTask) {
          setTasks((prevTasks) =>
            prevTasks.map((task) =>
              task.id === editingId ? originalTask : task,
            ),
          );
        }
        console.error("Failed to update task");
      }
    } catch (error) {
      // Revert on error
      if (originalTask) {
        setTasks((prevTasks) =>
          prevTasks.map((task) =>
            task.id === editingId ? originalTask : task,
          ),
        );
      }
      console.error("Error updating task:", error);
    }
  };

  const cancelEdit = () => {
    setEditingTaskId(null);
    setEditTask({
      description: "",
      assignee: "advisor",
      dueDate: "",
      clientId: 0,
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedAndFilteredTasks = tasks
    .filter(
      (task) =>
        (filterAssigneeType === "all" ||
          task.assignee === filterAssigneeType) &&
        (filterStatus === "all" ||
          (filterStatus === "pending" && !task.completed) ||
          (filterStatus === "complete" && task.completed)) &&
        (filterClient === "all" ||
          task.clientId.toString() === filterClient.toString()),
    )
    .sort((a: any, b: any) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === "dueDate" || sortField === "createdAt") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const filterOptions = [
    { name: "All Assignees", value: "all" },
    { name: "Advisor", value: "advisor" },
    { name: "Client", value: "client" },
  ];

  const statusFilterOptions = [
    { name: "All Statuses", value: "all" },
    { name: "Pending", value: "pending" },
    { name: "Complete", value: "complete" },
  ];

  const clientFilterOptions = [
    { name: "All Clients", value: "all" },
    ...clients.map((client) => ({ name: client.title, value: client.id })),
  ];

  const clientSelectOptions = clients.map((client) => ({
    name: client.title,
    value: client.id.toString(),
  }));

  return (
    <Layout page="tasks">
      <div className="border bg-white rounded-xl shadow-lg">
        <div className="flex justify-between items-center pt-8 ">
          <div className="p-6 w-full">
            <div className="flex items-center gap-3 mb-6">
              <h1 className="text-3xl font-bold text-gray-900">All Tasks</h1>
            </div>
            {/* Header with Add Button and Filters */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-4">
                <Select
                  selected={
                    filterOptions.find((v) => v.value === filterAssigneeType) ||
                    filterOptions[0]
                  }
                  setSelected={(v) => setFilterAssigneeType(v.value)}
                  options={filterOptions}
                />
                <Select
                  selected={
                    statusFilterOptions.find((v) => v.value === filterStatus) ||
                    statusFilterOptions[0]
                  }
                  setSelected={(v) => setFilterStatus(v.value)}
                  options={statusFilterOptions}
                />
                <Select
                  selected={
                    clientFilterOptions.find((v) => v.value === filterClient) ||
                    clientFilterOptions[0]
                  }
                  setSelected={(v) => setFilterClient(v.value)}
                  options={clientFilterOptions}
                />
              </div>

              <div className="text-sm text-gray-600">
                <Button
                  type="primary"
                  onClick={() => setShowAddForm(!showAddForm)}
                  className="flex items-center gap-2"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Task
                </Button>
              </div>
            </div>

            {/* Tasks Table */}
            <div className="overflow-x-auto overflow-y-auto max-h-[70vh] min-h-[500px] bg-white rounded-lg shadow">
              <table className="w-full border-collapse border border-gray-300 rounded-lg">
                <thead>
                  <tr className="bg-gray-50 text-sm text-center">
                    <th className="border border-gray-300 px-4 py-0">Status</th>
                    <th
                      className="border border-gray-300 px-4 py-0 cursor-pointer hover:bg-gray-200"
                      onClick={() => handleSort("description")}
                    >
                      <SortIcon name="Description" item="description" />
                    </th>
                    <th
                      className="border border-gray-300 px-4 py-0 cursor-pointer hover:bg-gray-200"
                      onClick={() => handleSort("clientName")}
                    >
                      <SortIcon name="Client" item="clientName" />
                    </th>
                    <th
                      className="border border-gray-300 px-4 py-0 cursor-pointer hover:bg-gray-200"
                      onClick={() => handleSort("assigneeType")}
                    >
                      <SortIcon name="Assignee" item="assigneeType" />
                    </th>
                    <th
                      className="border border-gray-300 px-4 py-0 cursor-pointer hover:bg-gray-200"
                      onClick={() => handleSort("dueDate")}
                    >
                      <SortIcon name="Due Date" item="dueDate" />
                    </th>
                    <th className="border border-gray-300 px-4 py-0">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {showAddForm && (
                    <tr className="bg-blue-50">
                      <td className="border border-gray-300 px-4 py-2">
                        <span className="text-sm text-blue-600">New</span>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <Input
                          value={newTask.description}
                          label=""
                          width="w-[250px] h-[100px]"
                          subtype="textarea"
                          setValue={(v) =>
                            setNewTask({
                              ...newTask,
                              description: v,
                            })
                          }
                          placeholder="Enter task description..."
                        />
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <Select
                          selected={
                            clientSelectOptions.find(
                              (c) => c.value === newTask.clientId.toString(),
                            ) || { name: "Select Client", value: "0" }
                          }
                          setSelected={(option) =>
                            setNewTask({
                              ...newTask,
                              clientId: parseInt(option.value),
                            })
                          }
                          options={clientSelectOptions}
                        />
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <Select
                          selected={{
                            name:
                              newTask.assignee === "advisor"
                                ? "Advisor"
                                : "Client",
                            value: newTask.assignee,
                          }}
                          setSelected={(option) =>
                            setNewTask({
                              ...newTask,
                              assignee: option.value as any,
                            })
                          }
                          options={[
                            { name: "Advisor", value: "advisor" },
                            { name: "Client", value: "client" },
                          ]}
                        />
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <Input
                          value={newTask.dueDate}
                          width="w-[170px]"
                          label=""
                          subtype="date"
                          setValue={(v) =>
                            setNewTask({
                              ...newTask,
                              dueDate: v,
                            })
                          }
                        />
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <div className="flex gap-1">
                          <Button
                            type="primary"
                            onClick={addTask}
                            className="px-2 py-1"
                          >
                            Save
                          </Button>
                          <Button
                            type="secondary"
                            onClick={() => setShowAddForm(false)}
                            className="px-2 py-1"
                          >
                            Cancel
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )}
                  {sortedAndFilteredTasks.length === 0 && !showAddForm ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="border border-gray-300 px-4 py-8 text-center text-gray-500"
                      >
                        No tasks found. Click "Add Task" to create your first
                        task.
                      </td>
                    </tr>
                  ) : (
                    sortedAndFilteredTasks.map((task) =>
                      editingTaskId === task.id ? (
                        <tr key={task.id} className="bg-yellow-50">
                          <td className="border border-gray-300 px-4 py-2">
                            <input
                              type="checkbox"
                              checked={task.completed}
                              onChange={() => toggleComplete(task.id)}
                              className="mr-2"
                            />
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <Input
                              value={editTask.description}
                              label=""
                              width="w-[250px] h-[100px]"
                              subtype="textarea"
                              setValue={(v) =>
                                setEditTask({
                                  ...editTask,
                                  description: v,
                                })
                              }
                            />
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <Select
                              selected={
                                clientSelectOptions.find(
                                  (c) =>
                                    c.value === editTask.clientId.toString(),
                                ) || { name: "Select Client", value: "0" }
                              }
                              setSelected={(option) =>
                                setEditTask({
                                  ...editTask,
                                  clientId: parseInt(option.value),
                                })
                              }
                              options={clientSelectOptions}
                            />
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <Select
                              selected={{
                                name:
                                  editTask.assignee === "advisor"
                                    ? "Advisor"
                                    : "Client",
                                value: editTask.assignee,
                              }}
                              setSelected={(option) =>
                                setEditTask({
                                  ...editTask,
                                  assignee: option.value as any,
                                })
                              }
                              options={[
                                { name: "Advisor", value: "advisor" },
                                { name: "Client", value: "client" },
                              ]}
                            />
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <Input
                              value={editTask.dueDate}
                              width="w-[170px]"
                              label=""
                              subtype="date"
                              setValue={(v) =>
                                setEditTask({
                                  ...editTask,
                                  dueDate: v,
                                })
                              }
                            />
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <div className="flex gap-1">
                              <Button
                                type="primary"
                                onClick={saveEdit}
                                className="px-2 py-1 text-sm"
                              >
                                Save
                              </Button>
                              <Button
                                type="secondary"
                                onClick={cancelEdit}
                                className="px-2 py-1 text-sm"
                              >
                                Cancel
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <tr
                          key={task.id}
                          className={`hover:bg-gray-50 ${task.completed ? "opacity-60" : ""}`}
                        >
                          <td className="border border-gray-300 px-4 py-2">
                            <input
                              type="checkbox"
                              checked={task.completed}
                              onChange={() => toggleComplete(task.id)}
                              className="mr-2"
                            />
                          </td>
                          <td
                            className={`border border-gray-300 px-4 py-2 ${task.completed ? "line-through" : ""}`}
                          >
                            {task.description}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 underline">
                            <Link to={`/client/${task.clientId}/map`}>
                              {task.client.title}
                            </Link>
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <span className="capitalize">
                              {task.assignee
                                ? task.assignee.replace("-", " ")
                                : "-"}
                            </span>
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            {task.dueDate ? (
                              <div>
                                <div
                                  className={
                                    new Date(task.dueDate) < new Date() &&
                                    !task.completed
                                      ? "text-red-500"
                                      : ""
                                  }
                                >
                                  {formatDate(task.dueDate)}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {new Date(task.dueDate) < new Date() &&
                                  !task.completed ? (
                                    <span className="text-red-500">
                                      Overdue
                                    </span>
                                  ) : new Date(task.dueDate).toDateString() ===
                                    new Date().toDateString() ? (
                                    <span className="text-orange-500">
                                      Due Today
                                    </span>
                                  ) : (
                                    <span className="text-gray-500">
                                      {Math.ceil(
                                        (new Date(task.dueDate).getTime() -
                                          new Date().getTime()) /
                                          (1000 * 60 * 60 * 24),
                                      )}{" "}
                                      days
                                    </span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <div className="flex gap-3">
                              <button
                                onClick={() => startEditing(task)}
                                className="hover:text-blue-700"
                                title="Edit task"
                              >
                                <PencilIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteTask(task.id)}
                                className="text-red-500 hover:text-red-700"
                                title="Delete task"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ),
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AllTasks;
